/**
 * Template Row
 *
 * Parent row for a single environment template plus a nested block of
 * sandbox sub-rows. The nested block shows the most relevant sandboxes
 * — up to 10 active, or (if no active exist) up to 4 most-recent
 * terminated/failed for context — and a "See all N sandboxes →" footer
 * link when there are more. The parent row's Launch button is the only
 * launch affordance.
 */
import React from 'react';
import { Button, Flex, Text } from '@chatsdet/ui';
import {
  ArrowRight,
  ArrowSquareOut,
  GitBranch,
  LinkSimple,
  Lock,
  Plus,
  WarningCircle,
} from '@phosphor-icons/react';
import { useNavigate } from '@tanstack/react-router';
import type { EnvironmentTemplate } from '../../../hooks/useEnvironmentTemplates';
import type {
  EnvironmentInstanceStatus,
  EnvironmentInstance,
} from '../../../hooks/useEnvironmentInstances';
import { InstanceStatusBadge } from './InstanceStatusBadge';
import { PurposeBadge } from './PurposeBadge';
import { SandboxStatusBadge } from './SandboxStatusBadge';
import { formatRelative } from './overview/time-format';
import { formatDurationFromIso, formatDurationMs } from '../../../lib/formatDuration';

const ACTIVE_STATUSES: EnvironmentInstanceStatus[] = [
  'pending',
  'provisioning',
  'deploying',
  'running',
  'converting',
  'stopping',
];
// Cap active rows at 10, and ensure at least 4 total rows by topping up with
// the most-recent inactive sandboxes when there aren't enough active ones.
const ACTIVE_LIMIT = 10;
const MIN_VISIBLE = 4;

function isActive(status: EnvironmentInstanceStatus): boolean {
  return ACTIVE_STATUSES.includes(status);
}

// Map Enter/Space on a button-styled <div> to its click handler so keyboard
// users can reach the same destinations as a pointer click.
function activateOnKey(handler: () => void) {
  return (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handler();
    }
  };
}

export interface TemplateRowProps {
  template: EnvironmentTemplate;
  instances: EnvironmentInstance[];
  onLaunch: (template: EnvironmentTemplate) => void;
}

export function TemplateRow({ template, instances, onLaunch }: TemplateRowProps) {
  const navigate = useNavigate();

  // Sort newest first so "most recent" is unambiguous for both buckets.
  const sorted = React.useMemo(
    () =>
      [...instances].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [instances]
  );

  const active = sorted.filter((i) => isActive(i.status));
  const inactive = sorted.filter((i) => !isActive(i.status));

  // Show all active (up to 10), then top up with the most-recent inactive
  // until we hit MIN_VISIBLE — so an idle template still feels populated
  // with prior-work breadcrumbs instead of looking like a dead row.
  const visibleActive = active.slice(0, ACTIVE_LIMIT);
  const fillCount = Math.max(0, MIN_VISIBLE - visibleActive.length);
  const visibleInactive = inactive.slice(0, fillCount);
  const visibleRows = [...visibleActive, ...visibleInactive];
  const hasMore = sorted.length > visibleRows.length;

  const goToTemplate = () =>
    navigate({
      to: '/sandboxes/templates/$templateId',
      params: { templateId: template.id },
    });

  const goToSandbox = (instanceId: string) =>
    navigate({ to: '/sandboxes/$sandboxId', params: { sandboxId: instanceId } });

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden">
      {/* Parent row */}
      <div
        className="flex items-center gap-4 p-4 hover:bg-muted/30 cursor-pointer transition-colors"
        role="button"
        tabIndex={0}
        onClick={goToTemplate}
        onKeyDown={activateOnKey(goToTemplate)}
      >
        <Flex direction="column" gap="xs" className="flex-1 min-w-0">
          <Flex align="center" gap="sm">
            <Text weight="semibold" className="truncate">
              {template.name}
            </Text>
            <SandboxStatusBadge status={template.status} />
          </Flex>
          <Flex align="center" gap="xs">
            {template.repositoryFullName ? (
              <>
                <Text size="xs" className="text-muted-foreground font-mono truncate">
                  {template.repositoryFullName}
                </Text>
                <GitBranch size={11} className="text-muted-foreground" />
                <Text size="xs" className="text-muted-foreground">
                  {template.branch}
                </Text>
              </>
            ) : (
              <Text size="xs" className="text-muted-foreground">
                No repository (empty workspace)
              </Text>
            )}
            <Text size="xs" className="text-muted-foreground">
              · {active.length} active
              {inactive.length > 0 ? ` · ${inactive.length} past` : ''}
            </Text>
          </Flex>
        </Flex>
        <Button
          variant="primary"
          size="sm"
          iconLeft={<Plus size={14} />}
          onPress={() => onLaunch(template)}
        >
          Launch
        </Button>
      </div>

      {/* Sub-rows */}
      <div className="border-t border-border bg-muted/10">
        {/* Middle: data rows */}
        {visibleRows.length === 0 ? (
          <div className="px-4 py-3">
            <Text size="sm" color="muted">
              No sandboxes yet — launch one to get started.
            </Text>
          </div>
        ) : (
          visibleRows.map((instance) => (
            <SandboxSubRow
              key={instance.id}
              instance={instance}
              onActivate={() => goToSandbox(instance.id)}
            />
          ))
        )}

        {/* Bottom: see-all footer */}
        {hasMore && (
          <button
            type="button"
            onClick={goToTemplate}
            className="w-full flex items-center justify-between px-4 py-2.5 text-left hover:bg-muted/30 transition-colors border-t border-border/60"
          >
            <Text size="sm" weight="medium" className="text-primary">
              See all {sorted.length} sandbox{sorted.length === 1 ? '' : 'es'}
            </Text>
            <ArrowRight size={14} className="text-primary" />
          </button>
        )}
      </div>
    </div>
  );
}

interface SandboxSubRowProps {
  instance: EnvironmentInstance;
  onActivate: () => void;
}

const TERMINAL_STATUSES = new Set<EnvironmentInstanceStatus>(['terminated', 'failed']);

// All statuses (including terminated/failed) are clickable: the detail page
// surfaces useful diagnostic info even for non-running instances.
function SandboxSubRow({ instance, onActivate }: SandboxSubRowProps) {
  const { primary, secondaryParts } = buildRowContent(instance);
  const showHealthWarning =
    instance.status === 'running' && instance.healthStatus === 'unhealthy';
  // For terminal states, the time-ago is more useful as "died X ago" than
  // "created X ago" — switch the anchor accordingly.
  const timeAnchor = TERMINAL_STATUSES.has(instance.status)
    ? instance.terminatedAt ?? instance.createdAt
    : instance.createdAt;

  return (
    <div
      className="flex items-start gap-3 px-4 py-3 border-b border-border/40 last:border-b-0 cursor-pointer hover:bg-muted/30 transition-colors"
      role="button"
      tabIndex={0}
      onClick={onActivate}
      onKeyDown={activateOnKey(onActivate)}
    >
      <div className="shrink-0 pt-0.5">
        <PurposeBadge purpose={instance.purpose} size="sm" />
      </div>

      <div className="flex-1 min-w-0 flex flex-col gap-1">
        <Text size="sm" weight="medium" className="truncate">
          {primary}
        </Text>
        {secondaryParts.length > 0 && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground min-w-0">
            {secondaryParts.map(({ key, node }, idx) => (
              <React.Fragment key={key}>
                {idx > 0 && (
                  <span aria-hidden className="text-muted-foreground/40">
                    ·
                  </span>
                )}
                {node}
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      <div className="shrink-0 flex items-center gap-3 pt-0.5">
        {showHealthWarning && (
          <WarningCircle
            size={15}
            weight="fill"
            className="text-amber-500"
            aria-label="Health check failing"
          />
        )}
        <InstanceStatusBadge status={instance.status} />
        <div className="min-w-[72px] flex justify-end">
          <SubRowAction instance={instance} />
        </div>
        <Text
          size="xs"
          color="muted"
          className="min-w-[56px] text-right tabular-nums shrink-0"
        >
          {formatRelative(timeAnchor)}
        </Text>
      </div>
    </div>
  );
}

// Decide what text goes in the primary line and which contextual fragments
// (slug, linked URL, status tail) should fill the secondary line. Pure
// derivation — keeps SandboxSubRow's body focused on layout.
function buildRowContent(instance: EnvironmentInstance): {
  primary: string;
  secondaryParts: { key: string; node: React.ReactNode }[];
} {
  const trimmedReason = instance.reason?.trim() || null;
  const reasonReplacesName = !!trimmedReason && trimmedReason !== instance.name;
  const primary = trimmedReason ?? instance.name;
  const linkedUrl = instance.linkedUrl ?? null;
  const linkedUrlLabel = linkedUrl ? formatLinkedUrl(linkedUrl) : null;
  const statusDetail = getStatusDetail(instance);

  const secondaryParts: { key: string; node: React.ReactNode }[] = [];
  if (reasonReplacesName) {
    secondaryParts.push({
      key: 'name',
      node: <span className="font-mono truncate">{instance.name}</span>,
    });
  }
  if (linkedUrl && linkedUrlLabel) {
    secondaryParts.push({
      key: 'url',
      node: (
        <a
          href={linkedUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-primary hover:underline max-w-[200px] truncate"
          onClick={(e) => e.stopPropagation()}
        >
          <LinkSimple size={11} weight="bold" className="shrink-0" />
          <span className="truncate">{linkedUrlLabel}</span>
        </a>
      ),
    });
  }
  if (statusDetail) {
    secondaryParts.push({
      key: 'detail',
      node: <span className="truncate">{statusDetail}</span>,
    });
  }
  return { primary, secondaryParts };
}

function SubRowAction({ instance }: { instance: EnvironmentInstance }) {
  if (instance.status !== 'running') return null;
  if (instance.isPrivate) {
    return (
      <Flex align="center" gap="xs">
        <Lock size={12} className="text-muted-foreground" />
        <Text size="xs" color="muted">
          Private
        </Text>
      </Flex>
    );
  }
  if (!instance.publicUrl) return null;
  return (
    <a
      href={instance.publicUrl}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 text-primary hover:underline text-xs whitespace-nowrap"
      onClick={(e) => e.stopPropagation()}
    >
      Open
      <ArrowSquareOut size={11} />
    </a>
  );
}

// Status-aware tail text for the secondary line — fills the row with the
// most actionable context for each lifecycle state.
function getStatusDetail(instance: EnvironmentInstance): string | null {
  switch (instance.status) {
    case 'pending':
      return 'Queued';
    case 'provisioning':
      return 'Provisioning…';
    case 'deploying':
      return 'Deploying…';
    case 'converting':
      return 'Converting to pool member…';
    case 'stopping':
      return 'Stopping…';
    case 'running':
      return instance.expiresAt ? `Expires ${formatExpiresIn(instance.expiresAt)}` : null;
    case 'failed':
      return instance.terminationReason?.trim() || 'Failed during setup';
    case 'terminated': {
      const reason = instance.terminationReason?.trim();
      if (reason) return reason;
      if (instance.startedAt && instance.terminatedAt) {
        return `Ran for ${formatDurationFromIso(instance.startedAt, instance.terminatedAt)}`;
      }
      return null;
    }
  }
}

function formatExpiresIn(expiresAt: string): string {
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return 'now';
  return `in ${formatDurationMs(ms)}`;
}

// Shorten a linked URL into a compact label. Recognizes common GitHub
// patterns (repo#123 for PRs/issues); falls back to the hostname.
function formatLinkedUrl(url: string): string {
  try {
    const u = new URL(url);
    const ghMatch = u.pathname.match(/^\/[^/]+\/([^/]+)\/(pull|issues)\/(\d+)/);
    if (ghMatch) return `${ghMatch[1]}#${ghMatch[3]}`;
    return u.hostname.replace(/^www\./, '');
  } catch {
    return 'link';
  }
}

TemplateRow.displayName = 'TemplateRow';
